import { useMutation } from '@apollo/client'
import { FormikHelpers } from 'formik'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SignUpSignIn from '..'
import RegisterForm from './Form'
import useToast from 'shared/components/Toast/useToast'
import { Register, ResendConfirmationEmail } from 'shared/graphql/auth.gql'
import { RegisterFormValues } from 'shared/models/user'
import { commonRouteConfig } from 'shared/routeConfig'
import { checkAuth } from 'shared/utils/auth'
import { getSubdomain } from 'shared/utils/routing'
import Confirmation from '../Confirmation'

const SignUp = () => {
    const navigate = useNavigate()
    const toast = useToast()
    const subdomain = getSubdomain(process.env.REACT_APP_DOMAIN)
    const [emailConfirmationSentTo, setEmailConfirmationSentTo] = useState('')
    const [registerInput, setRegisterInput] = useState<{
        firstName?: string
        lastName?: string
        email?: string
        role?: string
        phone?: string
    }>({})
    const [register] = useMutation(Register)
    const [resendConfirmationEmail] = useMutation(ResendConfirmationEmail)

    useEffect(() => {
        if (subdomain !== 'candidate') {
            navigate(commonRouteConfig.login.path, { replace: true })
            return
        }
        const isSignedIn = checkAuth()
        // TODO: get user role from token saved in local storage
        // dispatch(setRole(loginResponse.data.login.role))
        if (isSignedIn) {
            return navigate(commonRouteConfig.index.path, { replace: true }) //redirect
        }
    }, [navigate, subdomain])

    async function handleResendConfirmationEmail() {
        try {
            const response = await resendConfirmationEmail({
                variables: { input: { email: registerInput.email } },
            })
            if (response.data.resendConfirmationEmail.success) {
                toast({
                    severity: 'success',
                    title: 'Email sent',
                    message:
                        'Please check your email for the confirmation link',
                })
            }
        } catch (err) {
            const message = (err as any).message.replace('GraphQL error: ', '')
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
            console.log('Error', message)
            return null
        }
    }

    async function handleSubmit(
        values: RegisterFormValues,
        helpers: FormikHelpers<RegisterFormValues>
    ) {
        const { firstName, lastName, email, phone } = values
        const role = 'candidate'
        const registerInput = {
            firstName,
            lastName,
            email,
            role,
            phone,
        }
        try {
            const registerResponse = await register({
                variables: { input: registerInput },
            })
            if (registerResponse?.data?.register?.success) {
                setEmailConfirmationSentTo(email)
                setRegisterInput(registerInput)
            }
        } catch (err) {
            if (
                (err as any)?.graphQLErrors[0]?.extensions?.code ===
                'BAD_USER_INPUT'
            ) {
                toast({
                    title: 'Error',
                    message: 'Your email is already registered',
                })
                return null
            } else {
                const message = (err as any).message.replace(
                    'GraphQL error: ',
                    ''
                )
                // TODO: more detailed error message?
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
                console.log('Error', message)
                return null
            }
        } finally {
            helpers.setSubmitting(false)
        }
    }

    if (subdomain !== 'candidate') return null

    if (emailConfirmationSentTo)
        return (
            <Confirmation
                subtitle="Verify your Email"
                blurb={`We sent an email to ${emailConfirmationSentTo}. Click the link inside to get started.`}
                onClickResend={handleResendConfirmationEmail}
            />
        )

    return (
        <SignUpSignIn title="Registration">
            <RegisterForm onSubmit={handleSubmit} />
        </SignUpSignIn>
    )
}

export default SignUp
